import Image from 'next/image';
import { Fragment } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Abhaya_Libre } from 'next/font/google';
import ContentWrapper from '../ContentWrapper';
import testIds from './testIds';
import type FooterType from '../../../../types/Footer';
import AppDownloadLinks from '../AppDownloadLinks';
import { COPYRIGHT_FALLBACK, Locale } from '../../../core/constants';
import Link from '../Link/Link';

const abhayaLibre = Abhaya_Libre({
  weight: '800',
  subsets: ['latin'],
});

const CompanyLogo = (
  <Image src="/logo_white.svg" alt="logo" width={123} height={35} layout="fixed" />
);

const CompanyLogoMobile = (
  <Image src="/logo_white.svg" alt="logo" width={123} height={35} layout="fixed" />
);

type Props = {
  copyright: FooterType['copyright'];
  links: NonNullable<FooterType['footerLinks']>;
  spaceForStickyBar?: boolean;
  disableCompanyLogoLink?: boolean;
  hotelCount: number;
};

const Footer = ({
  copyright = COPYRIGHT_FALLBACK,
  links,
  spaceForStickyBar = false,
  disableCompanyLogoLink = false,
  hotelCount,
}: Props) => {
  const router = useRouter();
  const { t } = useTranslation();
  const youtubeUrl = process.env.NEXT_PUBLIC_YOUTUBE_URL || '#';
  const jaInstagramUrl = process.env.NEXT_PUBLIC_JA_INSTAGRAM_URL || '#';
  const enInstagramUrl = process.env.NEXT_PUBLIC_EN_INSTAGRAM_URL || '#';
  const lineUrl = process.env.NEXT_PUBLIC_LINE_URL || '#';
  const twitterUrl = process.env.NEXT_PUBLIC_TWITTER_URL || '#';

  return (
    <footer className="z-20 bg-footerBgColor py-10 text-white sm:pb-12 sm:pt-14">
      <ContentWrapper className="mb-8 flex flex-col items-center gap-10 lg:items-start">
        <div className="mb-3 flex flex-1 flex-col items-center gap-10 md:gap-4 lg:items-start">
          <div className="hidden h-9 sm:block">
            {disableCompanyLogoLink ? (
              CompanyLogo
            ) : (
              <Link href="/" prefetch={false}>
                <a target="_self" className="h-full cursor-pointer sm:pr-8">
                  {CompanyLogo}
                </a>
              </Link>
            )}
          </div>
          <div className="w-fit sm:hidden">
            {disableCompanyLogoLink ? (
              CompanyLogoMobile
            ) : (
              <Link href="/" prefetch={false}>
                <a target="_self" className="cursor-pointer sm:pr-8">
                  {CompanyLogoMobile}
                </a>
              </Link>
            )}
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col-reverse items-center gap-6 lg:flex-row">
              <h3 className="bg-koiRed px-4 py-2 text-base font-bold">
                「あなたのストーリーを紡ぐ旅をTabistで」
              </h3>
              <h3
                className={`whitespace-pre-wrap text-center text-[22px] sm:whitespace-nowrap ${abhayaLibre.className}`}
              >
                {`Craft your journey with Tabist.\nWeave a story that's uniquely yours.`}
              </h3>
            </div>
            <p className="whitespace-pre-wrap text-center text-sm sm:whitespace-normal sm:text-left">
              {t('footer.description', {
                hotelCount: hotelCount || 222,
              })}
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col-reverse items-center gap-10 sm:flex-row">
          <div
            data-testid={testIds.link_wrapper_desktop}
            className="hidden w-8/12 grid-cols-1 gap-x-4 sm:grid lg:grid-cols-2"
          >
            {links.map((x, i) => (
              <div
                key={x.text}
                className={`pb-4  ${links.length - 1 === i ? 'lg:col-span-2' : ''}`}
              >
                <Link href={x.url || '#'} prefetch={false}>
                  <a
                    target={x.target}
                    rel={x.target === '_blank' ? 'noopener' : ''}
                    className="mb-1 mr-2 font-bold text-white"
                  >
                    {x.text}
                  </a>
                </Link>
              </div>
            ))}
          </div>
          <div>
            {router.locale === Locale.JAPANESE && (
              <div className="text-md hidden sm:flex">
                <AppDownloadLinks />
              </div>
            )}
            <div className="hidden flex-1 justify-center gap-8 pt-2 sm:flex">
              <Link href={youtubeUrl} prefetch={false}>
                <a target="_blank" rel="noopener" className="flex items-center">
                  <Image
                    src="/youtube.svg"
                    alt="youtube logo"
                    width={33}
                    height={23}
                    layout="fixed"
                  />
                </a>
              </Link>
              <Link
                href={router?.locale === Locale.JAPANESE ? jaInstagramUrl : enInstagramUrl}
                prefetch={false}
              >
                <a target="_blank" rel="noopener" className="flex items-center">
                  <Image
                    src="/instagram.svg"
                    alt="instagram logo"
                    width={27}
                    height={27}
                    layout="fixed"
                  />
                </a>
              </Link>
              <Link href={twitterUrl} prefetch={false}>
                <a target="_blank" rel="noopener" className="flex items-center">
                  <Image src="/x.svg" alt="x logo" width={30} height={24} layout="fixed" />
                </a>
              </Link>
              <Link href={lineUrl} prefetch={false}>
                <a target="_blank" rel="noopener" className="flex items-center">
                  <Image
                    src="/line_icon.svg"
                    alt="line logo"
                    width={27}
                    height={27}
                    layout="fixed"
                  />
                </a>
              </Link>
            </div>
          </div>
          <div className="mb-5 flex flex-1 justify-center gap-6 sm:hidden">
            <Link href={youtubeUrl} prefetch={false}>
              <a target="_blank" rel="noopener" className="flex items-center">
                <Image
                  src="/youtube.svg"
                  alt="youtube logo"
                  width={32}
                  height={32}
                  layout="fixed"
                />
              </a>
            </Link>
            <Link
              href={router?.locale === Locale.JAPANESE ? jaInstagramUrl : enInstagramUrl}
              prefetch={false}
            >
              <a target="_blank" rel="noopener" className="flex items-center">
                <Image
                  src="/instagram.svg"
                  alt="instagram logo"
                  width={32}
                  height={32}
                  layout="fixed"
                />
              </a>
            </Link>
            <Link href={twitterUrl} prefetch={false}>
              <a target="_blank" rel="noopener" className="flex items-center">
                <Image src="/x.svg" alt="x logo" width={32} height={32} layout="fixed" />
              </a>
            </Link>
            <Link href={lineUrl} prefetch={false}>
              <a target="_blank" rel="noopener" className="flex items-center">
                <Image src="/line_icon.svg" alt="line logo" width={32} height={32} layout="fixed" />
              </a>
            </Link>
          </div>
          {router.locale === Locale.JAPANESE && (
            <div className="text-xs sm:hidden">
              <AppDownloadLinks />
            </div>
          )}
          <div className="flex w-11/12 flex-col flex-wrap items-center justify-center gap-2 sm:hidden">
            {links.map((x) => (
              <Fragment key={x.text}>
                <Link href={x.url || '#'} prefetch={false}>
                  <a
                    target={x.target}
                    rel={x.target === '_blank' ? 'noopener' : ''}
                    className="mb-2 px-2 text-sm text-white"
                  >
                    {x.text}
                  </a>
                </Link>
              </Fragment>
            ))}
          </div>
        </div>
      </ContentWrapper>

      <ContentWrapper>
        <div className="mt-5 text-center sm:mt-8">
          <p className="text-mini">{copyright}</p>
        </div>
      </ContentWrapper>

      {/* add space at the bottom when the sticky price bar is being shown to prevent it from covering/hiding footer contents */}
      {spaceForStickyBar && <div className="h-24 sm:h-30" />}
    </footer>
  );
};

export default Footer;
